import { postMessageWithPromise } from "../../../base/browser/postMessage";
import { toDisposable } from "../../../base/common/lifecycle";
import { safeStringify } from "../../../base/common/objects";
import { BUILD_ENVIRONMENT, BUILD_HASH, BUILD_PLATFORM, BUILD_SEMVER } from "../../../base/common/$__metadata.js";
import { IIdentitiyService } from "../../auth/common/auth";
import { ILogService } from "../../log/common/log";
import { IProductService } from "../../product/common/productService";
import { IStorageService, StorageScope, StorageTarget } from "../../storage/common/storage";
import { BaseAnalyticsService, analyticsErrorDetails } from "../common/analyticsService";
// import rybbit from "@rybbit/js";


export class RybbitAnalyticsService extends BaseAnalyticsService {

    private _identityService: IIdentitiyService | null = null;

    // A setter without a getter reads back as `undefined`, which silently broke
    // all user attribution (identify + per-event user_id) — keep both accessors.
    get identityService(): IIdentitiyService | null {
        return this._identityService;
    }

    set identityService(value: IIdentitiyService | null) {
        if (this._identityService) throw new Error("RybbitAnalyticsService: identityService can only be set once");
        this._identityService = value;
        if (this._identityService) {
            this._identityService.onDidChange(() => {
                this.identify();
            });
            if (this._identityService.me) this.identify();
        }
    }

    constructor(
        private readonly config: {
            readonly siteId: string,
            trackErrors?: boolean,
            /**
             * When true, only errors whose stack originates from the extension itself
             * (a chrome-extension:// frame) are reported. Use this for the browser
             * extension, which is injected into arbitrary third-party pages and would
             * otherwise capture the host page's errors as noise.
             */
            onlyExtensionErrors?: boolean,
            /**
             * Send events through the extension's postMessage bridge → service
             * worker instead of fetching directly. Page-context fetches obey the
             * HOST page's CSP, so strict connect-src allowlists silently block
             * telemetry; worker fetches follow the extension's own permissions.
             * Extension build only (requires the content-script bridge).
             */
            useExtensionBridge?: boolean,
        },
        @ILogService private readonly logger: ILogService,
        @IStorageService private readonly storageService: IStorageService,
        @IProductService private readonly productService: IProductService,
    ) {
        super();

        // track page url changes
        const pageViewEvent = (state: PopStateEvent) => {
            this.onLogEvent("pageview", {
                reason: "popstate"
            });
        };
        window.addEventListener("popstate", pageViewEvent);
        this._register(toDisposable(() => {
            window.removeEventListener("popstate", pageViewEvent);
        }));

        if (config.trackErrors) {
            this.logger.info("RybbitTel: Error tracking enabled");
            window.addEventListener("error", this.onError);
            this._register(toDisposable(() => { window.removeEventListener("error", this.onError); }));
            window.addEventListener("unhandledrejection", this.onUnhandledRejection);
            this._register(toDisposable(() => { window.removeEventListener("unhandledrejection", this.onUnhandledRejection); }));
        }
    }

    /**
     * Best-effort HOST-PAGE location. This service can run in the isolated
     * inspect window / popout, whose `window.location` is `about:blank` or a
     * blob URL — an EMPTY hostname. That's why render-loop errors
     * (`inspector.animation_loop`) landed with no site attribution: they were
     * stamped with the popout's blank location, not the inspected page's. Walk
     * to the real host — same-origin top frame, then the opener that spawned the
     * popout — before falling back to our own location. Cross-origin access
     * throws, so every hop is guarded. (Per CLAUDE.md: never assume the
     * module-scope `window` is the host — the active window changes at runtime.)
     */
    private resolveHostLocation(): { hostname: string; pathname: string; host: string } {
        for (const w of [window, window.top, window.opener] as (Window | null | undefined)[]) {
            try {
                const loc = w?.location;
                if (loc?.hostname) return { hostname: loc.hostname, pathname: loc.pathname, host: loc.host };
            } catch {
                // cross-origin frame/opener — inaccessible, try the next candidate
            }
        }
        // Nothing reachable had a hostname (isolated blob with no same-origin host).
        return { hostname: window.location.hostname, pathname: window.location.pathname, host: window.location.host };
    }

    protected onLogEvent(eventName: "pageview" | ({} & string), params?: { [key: string]: any; }, opts?: { type?: "error" }): void {

        params = params || {};
        if (opts?.type === "error" || eventName.startsWith("auth:")) {
            params = { ...params, version: BUILD_SEMVER, hash: BUILD_HASH, build: BUILD_ENVIRONMENT, platform: BUILD_PLATFORM };
        }
        const location = this.resolveHostLocation();
        // include the host on every event (not just pageviews) so custom events and errors
        // can be segmented by the site the extension runs on (local dev vs in-the-wild sites)
        if (!params["host"]) params["host"] = location.host;

        // Rybbit only attributes events to a user via the top-level `user_id` field of the
        // track payload (properties are opaque JSON). Fall back to the persisted id so events
        // fired before the identity service has resolved the session are still attributed;
        // the key is cleared on logout in identify().
        const userId = this._identityService?.me?.id
            || this.storageService.get("rum-user-id", StorageScope.APPLICATION)
            || undefined;


        // https://rybbit.com/docs/api/sending-events#parameters
        let url: string | null = null;
        let body: Record<string, any> | null = null;
        const type = opts?.type || "custom_event";

        switch (eventName) {
            case "pageview": {
                url = "https://needle.tools/api/v1/rum/t";
                body = {
                    site_id: this.config.siteId,
                    type: "pageview",
                    user_id: userId,
                    pathname: location.pathname,
                    hostname: location.hostname,
                    page_title: document.title,
                    referrer: document.referrer,
                    user_agent: navigator.userAgent,
                    // NO querystring: in the extension this is the INSPECTED
                    // page's, and real query strings carry session tokens,
                    // signed URLs and OAuth codes. Nothing queries it; a
                    // credential landing in the analytics store is an
                    // incident, not a privacy preference.
                    language: navigator.language,
                    screenWidth: window.screen.width,
                    screenHeight: window.screen.height,
                    event_name: eventName,
                    properties: params ? safeStringify(params) : undefined,
                };
                break;
            }
            default: {
                url = "https://needle.tools/api/v1/rum/t"
                body = {
                    site_id: this.config.siteId,
                    type: type,
                    user_id: userId,
                    pathname: location.pathname,
                    hostname: location.hostname,
                    event_name: eventName,
                    properties: params ? safeStringify(params) : undefined,
                }
                break;
            }
        }

        this.logger.debug("RybbitTel: Sending event", eventName);
        this.sendFetch(url, body);
    }


    private identify() {

        const user = this.identityService?.me;
        const userId = user?.id;
        if (!user || !userId?.trim()?.length) {
            this.storageService.store("rum-user-id", null, StorageScope.APPLICATION, StorageTarget.USER);
            this.logger.debug("RybbitTel: identify skipped, user not logged in");
            return;
        }

        try {
            this.storageService.store("rum-user-id", userId, StorageScope.APPLICATION, StorageTarget.USER);
        } catch (e) {
            this.logger.error("Could not persist user ID to localStorage");

        }

        const url = "https://needle.tools/api/v1/rum/identify"
        const body = {
            site_id: this.config.siteId,
            user_id: user.id,
            traits: {
                username: user.name || undefined,
                email: user.email || undefined,
                avatar_url: user.avatarUrl || undefined,
            },
            is_new_identify: true,
        };
        this.logger.debug("RybbitTel: Sending identify", body);
        this.sendFetch(url, body);
        this.logger.debug("RybbitTel: Identify sent", body);
    }

    private sendFetch(url: string, body: Record<string, any>) {

        // Dev builds must not pollute production analytics (a single dev machine
        // dominated the upsell funnel). Events are logged locally instead.
        if (this.productService.environment === "dev") {
            this.logger.debug("RybbitTel: dev build — event suppressed", body.event_name ?? url, body);
            return;
        }

        if (this.config.useExtensionBridge) {
            // Relay via content-script bridge → service worker (see
            // service-worker.analytics.js): the worker's fetch is governed by
            // extension permissions, not the host page's CSP. Fire-and-forget.
            postMessageWithPromise({ type: "needle:analytics:fetch", url, body }, undefined, { timeout: 10_000 })
                .then((response) => {
                    if (response?.success === false) this.logger.debug("RybbitTel: bridge send failed", response.error);
                })
                .catch(() => { /* bridge absent or timed out — drop the event */ });
            return;
        }

        fetch(url, {
            method: "POST",
            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json'
            },
            // Ensures request completes even if page unloads
            keepalive: true,
            // Allow CORS requests
            mode: 'cors',
            // Low priority to avoid blocking other requests
            priority: 'low',
        }).catch((error) => {
            this.logger.warn("RybbitTel: Failed to send event", error);
        });
    }



    override logError(error: unknown, context?: Record<string, unknown>): void {
        const { name, ...details } = analyticsErrorDetails(error);
        void this.trackError(name, details, context, true);
    }

    // #region error tracking
    private onError = (event: ErrorEvent) => {
        this.logger.info("RybbitTel: Caught global error event");
        const errorData: ErrorData = {
            message: (event.message || "Unknown error").substring(0, 500),
            stack: (event.error?.stack || "").substring(0, 2000),
            filename: (event.filename)?.substring(0, 200),
            lineno: event.lineno,
            colno: event.colno,
            timestamp: Date.now(),
        };
        this.trackError(event.error?.name || "Error", errorData);
    }
    private onUnhandledRejection = (event: PromiseRejectionEvent) => {
        this.logger.info("RybbitTel: Caught unhandled rejection event");
        let errorData: ErrorData;
        if (event.reason instanceof Error) {
            errorData = {
                message: event.reason.message.substring(0, 500),
                stack: event.reason.stack?.substring(0, 2000),
                timestamp: Date.now(),
            };
        } else {
            const reasonString = typeof event.reason === "string" ? event.reason : safeStringify(event.reason);
            errorData = {
                message: reasonString.substring(0, 500),
                timestamp: Date.now(),
            };
        }
        this.trackError("UnhandledRejection", errorData);
    }

    private _errorTrackingCache = new Map<string, { time: number, count: number }>();

    private async trackError(errorName: string, error: ErrorData, context?: { [key: string]: any; }, explicitlyReported = false) {
        try {
            // The extension is injected into arbitrary third-party pages, so the global error
            // handlers also catch the host page's errors. When configured, only report errors
            // that originate from the extension's own code (a chrome-extension:// stack frame).
            if (!explicitlyReported && this.config.onlyExtensionErrors && !error.stack?.includes("chrome-extension://")) {
                this.logger.debug(`RybbitTel: Skipping non-extension error ${errorName}`);
                return;
            }

            const errorKey = `${errorName}|${error.message}|${error.filename}|${error.lineno}|${error.colno}`;
            const now = Date.now();

            const cached = this._errorTrackingCache.get(errorKey);
            // don't log the same error too often
            const timeThreshold = 1 * 60 * 1000;
            if (cached && now - cached.time < timeThreshold) {
                this.logger.debug(`RybbitTel: Skipping error log for ${errorName}, already logged recently`);
                cached.count++;
                return;
            }
            this._errorTrackingCache.set(errorKey, { time: now, count: (cached?.count || 0) + 1 });

            // https://github.com/rybbit-io/rybbit-js/blob/c7cf42abb7743b2770852152556fc0486d261096/src/errorTracking.ts#L99
            const properties: Record<string, any> = {
                error_name: errorName,
                message: typeof error === "string" ? error : error.message,
                ...context
            };

            if (cached && cached?.count > 1) {
                properties.error_name += ` x${cached.count}`;
            }

            if (error.stack) properties.stack = error.stack;
            if (error.filename) properties.filename = error.filename;
            if (error.lineno) properties.line_number = error.lineno;
            if (error.colno) properties.column_number = error.colno;
            if (error.timestamp) properties.timestamp = error.timestamp;

            try {
                const errorLine = await tryGetErrorLine(error.stack).catch((e) => { this.logger.error(e) /* ignoring */ })
                if (errorLine) {
                    properties.stack += `\nCode: ${errorLine}`;
                }

            }
            catch (e) {
                this.logger.warn("Failed to get error line", e);
            }

            this.onLogEvent(errorName, properties, { type: "error" });
        }
        catch (e) {
            this.logger.warn("Failed to track error", e);
        }
    }
}

type ErrorData = {
    message?: string;
    stack?: string;
    filename?: string;
    lineno?: number;
    colno?: number;
    timestamp?: number;
}

const previousErrorLines: Map<string, Promise<string | null>> = new Map();
const sourcecodePromise: Map<string, Promise<string>> = new Map();

async function tryGetErrorLine(stack: string | undefined): Promise<string | null> {
    if (stack) {
        // find first occurance of at chrome-extension:// and capture filepath with :linenumber:columnnumber
        const line = /at (?<filepath>chrome-extension:\/\/[^\s]+):(?<linenumber>\d+):(?<columnnumber>\d+)/.exec(stack);
        if (line) {
            const filepath = line.groups?.filepath;
            const linenumber = line.groups?.linenumber;
            const columnnumber = line.groups?.columnnumber;
            const key = `${filepath}|${linenumber}|${columnnumber}`;

            let previousPromise = previousErrorLines.get(key);
            if (previousPromise) {
                return previousPromise;
            }

            const promise = (async () => {
                if (filepath && linenumber !== undefined) {
                    let promise = sourcecodePromise.get(filepath);
                    if (!promise) {
                        promise = fetch(filepath).then(res => res.text());
                        sourcecodePromise.set(filepath, promise);
                    }

                    return promise.then(sourcecode => {
                        const lines = sourcecode.split(/\r?\n/);
                        const lineNumber = parseInt(linenumber, 10);
                        if (lineNumber > 0 && lineNumber <= lines.length) {
                            const errorLine = lines[lineNumber - 1];
                            let result = errorLine;
                            if (columnnumber !== undefined) {
                                const col = parseInt(columnnumber, 10);
                                const startIndex = Math.max(0, col - 20);
                                const endIndex = Math.min(errorLine.length, col + 50);
                                result = errorLine.substring(startIndex, endIndex);
                            }
                            return result.trim();
                        }
                        return null;
                    });
                }
                return null;
            })();

            previousErrorLines.set(key, promise);
            return promise;
        }
    }

    return null;
}
